import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useOutletContext, useParams } from 'react-router-dom';

import { Button as MuiButton, Grid, Stack } from '@mui/material';
import { AxiosResponse } from 'axios';

import { KaseApi, MasterIndexApi, MasterIndexApiPath } from '@/api';
import {
  DisabledOverlay,
  FormRoot,
  RenderFormField,
  FormCard,
  Loader,
  PageHeader,
} from '@/components';
import NavigationButtonGroup from '@/components/NavigationButtonGroup/NavigationButtonGroup';
import { useFilesValidation } from '@/hooks';
import { TabAnimationWrapper, useTabLayoutContext } from '@/layouts/TabLayout';
import {
  IMasterNameOrganization,
  IMasterNameOrganizationForm,
  IMasterOrganizationRequest,
} from '@/models';
import { MasterIndexDetailsContext } from '@/pages/MasterIndex/components/MasterIndexDetailsPage';
import MasterIndexRelatedRecords from '@/pages/MasterIndex/components/MasterIndexRelatedRecords/MasterIndexRelatedRecords';
import MasterIndexOrganizationNotes from '@/pages/MasterIndex/Organizations/Notes';
import { uploadFiles } from '@/services';
import { ConfirmTypes, useToastStore } from '@/store';
import { formatPhoneNumber, generateUniqueId, parsePhoneNumber } from '@/utils';

import { AdditionanInformationForm } from './AdditionanInformationForm';
import {
  InformationTypes,
  InformationValues,
  MasterNameKaseOrganizationFields,
  TabNamesNextPaths,
  TabNamesScreens,
  infoTypeMap,
} from '../data';
import { useMasterNamesContext } from '../MasterNamesContext';

type MasterNameKaseOrganizationFormProps = {
  hideHeader?: boolean;
  isMasterIndex?: boolean;
  onGoBack?: () => void;
};

export const MasterNameKaseOrganizationForm = ({
  hideHeader,
  isMasterIndex,
  ...props
}: MasterNameKaseOrganizationFormProps) => {
  const outletContext = useOutletContext<
    MasterIndexDetailsContext | undefined
  >();
  const setCustomTitle = outletContext?.setCustomTitle;
  const setLastUpdatedAt = outletContext?.setLastUpdatedAt;
  const { kaseId, cfsId, id: organizationId } = useParams();

  const { updateToast } = useToastStore();
  const methods = useForm<IMasterNameOrganizationForm>({
    mode: 'all',
    defaultValues: {
      isVerified: false,
    },
  });

  const { currentMastername, linkedMasterNames } = useMasterNamesContext();
  const { updateCurrentScreen } = useTabLayoutContext();
  const { handleSubmit, watch } = methods;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState('information');

  const uploadedFileItems = watch('files');
  const hasInvalidFiles = useFilesValidation(uploadedFileItems);

  const onGoBack = () => {
    if (props.onGoBack) {
      return props.onGoBack();
    }
    updateCurrentScreen(TabNamesScreens.NamesList);
  };

  const fetchMasterName = async () => {
    if (organizationId) {
      return MasterIndexApi.getMasterOrganization(organizationId);
    }
  };

  const getItemValue = (item: Record<string, any>) => {
    const allValues = Object.values(item);
    const allKeys = Object.keys(item);

    if (allKeys.includes('phoneNumber')) {
      item.phoneNumber = parsePhoneNumber(item.phoneNumber);
    }
    return allValues.length > 1 ? item : allValues[0];
  };

  const onSubmit = async (values: IMasterNameOrganizationForm) => {
    if (!kaseId && !cfsId && !isMasterIndex) {
      return updateToast({
        type: ConfirmTypes.ERROR,
        open: true,
        message: 'invalid case id',
      });
    }

    const formattedInfoTypeData = Object.values(InformationTypes).reduce(
      (accumulatedInfoType, infoType) => {
        const typeItems = values.informationType
          .filter((valueItem) => !!valueItem.name)
          .find((valueItem) => valueItem.name === infoType);

        const relevantFieldNames = (
          infoTypeMap[infoType as keyof typeof infoTypeMap]?.InputFields ?? []
        ).map((f: { name: string }) => f.name);

        return {
          ...accumulatedInfoType,
          [infoType]: typeItems
            ? typeItems.items
                .map((item: Record<string, any>) => {
                  const cleaned = relevantFieldNames.reduce(
                    (acc: Record<string, any>, key: string) => {
                      if (key && key in item) acc[key] = item[key];
                      return acc;
                    },
                    {},
                  );
                  return getItemValue(cleaned);
                })
                .filter((item: any) => {
                  if (typeof item === 'object' && item !== null) {
                    return Object.values(item).some(
                      (v) => v !== '' && v !== undefined && v !== null,
                    );
                  }
                  return item !== '' && item !== undefined && item !== null;
                })
            : [],
        };
      },
      {},
    );

    try {
      setIsLoading(true);
      if (values.files && values.files.length) {
        const uploadedFiles = await uploadFiles(values.files);

        if (!uploadedFiles) return;

        values.files = uploadedFiles;
      }

      const isUpdatePayload = !!values._id && values._id !== 'undefined';
      const data: IMasterOrganizationRequest = {
        ...values,
        ...formattedInfoTypeData,
        addresses: isUpdatePayload
          ? values.address
            ? [values.address]
            : []
          : values.address
          ? [values.address]
          : undefined,
        address: isUpdatePayload
          ? values.address?._id ?? ''
          : values.address?._id,
      };

      let result: AxiosResponse<IMasterNameOrganization, any>;
      if (isMasterIndex) {
        if (data._id) {
          result = await MasterIndexApi.updateMasterOrganization(data);
          updateToast({
            type: ConfirmTypes.SUCCESS,
            open: true,
            message: `Organization updated successfully`,
          });
        } else {
          result = await MasterIndexApi.createMasterOrganization(data);
          updateToast({
            type: ConfirmTypes.SUCCESS,
            open: true,
            message: `Organization created successfully`,
          });
        }
      } else {
        result = await KaseApi.saveMasterOrganization(data);
        if (!result.data?._id) {
          return updateToast({
            type: ConfirmTypes.ERROR,
            open: true,
            message: `Organization NOT SAVED`,
          });
        }
        const isUpdate = data._id && data._id !== 'undefined';

        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: isUpdate
            ? `Organization updated successfully`
            : `Organization created successfully`,
        });
        const alreadyLined = linkedMasterNames.find(
          (linked) => linked.masterName._id === result.data._id,
        );
        if (!alreadyLined) {
          await KaseApi.addKaseMasterName(String(kaseId), result.data._id);
        }
      }

      onGoBack();
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    } finally {
      setIsLoading(false);
    }
    return values;
  };

  const getInformationType = (masterName: IMasterNameOrganization) => {
    const informationType = Object.values(InformationTypes).reduce(
      (acc, infoType) => {
        const typeItems = masterName[infoType];
        if (!typeItems?.length) {
          return [...acc];
        }

        const items =
          typeof typeItems[0] === 'object'
            ? [
                ...typeItems.map((typeItem) =>
                  Object.entries(typeItem).reduce(
                    (accumulatedTypes, [key, value]) => {
                      return key === 'phoneNumber'
                        ? {
                            ...accumulatedTypes,
                            [key]: parsePhoneNumber(value),
                          }
                        : { ...accumulatedTypes, [key]: value };
                    },
                    {},
                  ),
                ),
              ]
            : formatFieldNames(typeItems as string[], infoType);

        return typeItems?.length
          ? [
              ...acc,
              {
                name: infoType,
                items,
              },
            ]
          : [...acc];
      },
      [] as Array<Record<string, any>>,
    );
    return informationType;
  };

  const formatFieldNames = (
    typeItems: string[],
    infoType: InformationValues,
  ) => {
    if (!typeItems || typeItems.length === 0) return [];

    return typeItems.map((item) => {
      const itemTypeFields = infoTypeMap[infoType]?.InputFields;
      if (!itemTypeFields || itemTypeFields.length === 0) return {};

      const fieldName = itemTypeFields[0]?.name;
      if (fieldName?.includes('phoneNumber')) {
        item = formatPhoneNumber(item);
      }
      return fieldName ? { [fieldName]: item } : {};
    });
  };

  useEffect(() => {
    const resetForm = (masterName: IMasterNameOrganization) => {
      methods.reset({
        ...masterName,
        address: masterName.addresses?.[0] || masterName.address,
        phoneNumbers: masterName.phoneNumbers?.map((item) => ({
          phoneNumber: parsePhoneNumber(item.phoneNumber),
          type: item.type,
        })),
        informationType: getInformationType(masterName),
      });
    };

    if (!currentMastername && !organizationId) return;

    if (isMasterIndex && organizationId) {
      fetchMasterName()
        .then((response) => {
          if (!response) return;
          resetForm({
            ...response.data,
            address: response.data.addresses?.[0] || response.data.address,
          } as IMasterNameOrganization);
          if (response.data.name && setCustomTitle) {
            setCustomTitle(
              response.data.uniqueId
                ? `${generateUniqueId('ORG', response.data.uniqueId)} - ${
                    response.data.name
                  }`
                : response.data.name,
            );
          }
          setLastUpdatedAt?.(response.data?.updatedAt);
        })
        .catch((error) => {
          updateToast({ open: true, message: error.message });
        });
    } else {
      resetForm(currentMastername?.masterName as IMasterNameOrganization);
    }
  }, [currentMastername, isMasterIndex, organizationId]);

  const getTabs = () => {
    return [
      {
        label: 'Organization information',
        path: 'information',
      },
      {
        label: 'Related records',
        path: 'related',
        disabled: !currentMastername?.masterName?._id && !organizationId,
      },
      {
        label: 'Notes',
        path: 'notes',
        disabled: !currentMastername?.masterName?._id && !organizationId,
      },
    ];
  };

  const tabs = getTabs();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'information':
        return (
          <FormProvider {...methods}>
            <FormRoot
              id={isMasterIndex ? 'master-index-form' : undefined}
              onSubmit={handleSubmit(onSubmit)}
              sx={{
                pb: 0,
                '.p-kase-edit &': {
                  padding: '20px',
                  mt: 0,
                },
              }}
            >
              {MasterNameKaseOrganizationFields.map(({ key, title, items }) => {
                return (
                  <FormCard key={key} title={title} className="grey-title">
                    {items.map(({ grid, ...rest }: any, index: number) => {
                      return (
                        <Grid
                          key={`${key} - ${index}`}
                          xs={12}
                          sm={6}
                          md={3}
                          {...grid}
                          {...rest}
                          item
                        >
                          {rest.name === 'informationTypes' ? (
                            <AdditionanInformationForm />
                          ) : (
                            <RenderFormField {...rest} />
                          )}
                        </Grid>
                      );
                    })}
                  </FormCard>
                );
              })}
            </FormRoot>
          </FormProvider>
        );

      case 'related':
        return (
          <MasterIndexRelatedRecords
            type={MasterIndexApiPath.Organizations}
            viewId={currentMastername?.masterName._id || organizationId}
          />
        );

      case 'notes':
        return (
          <MasterIndexOrganizationNotes
            viewId={currentMastername?.masterName._id || organizationId}
          />
        );

      default:
        return null;
    }
  };

  const tabsWithHandlers = tabs.map((tab) => ({
    ...tab,
    onClick: () => setActiveTab(tab.path),
  }));

  const getOrganizationTitle = () => {
    const masterName = currentMastername?.masterName as IMasterNameOrganization;

    if (masterName?.name) {
      if (masterName.uniqueId && !isMasterIndex) {
        return `${generateUniqueId('ORG', masterName.uniqueId)} - ${
          masterName.name
        }`;
      }
      return masterName.name;
    }

    return 'New organization';
  };

  const renderActionButtons = () => {
    if (activeTab !== 'information') {
      return null;
    }

    return (
      <Stack direction="row" spacing={2}>
        <MuiButton color="inherit" variant="outlined" onClick={onGoBack}>
          Cancel
        </MuiButton>
        <MuiButton
          color="error"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={hasInvalidFiles}
        >
          Save
        </MuiButton>
      </Stack>
    );
  };

  return (
    <>
      <TabAnimationWrapper nextPaths={TabNamesNextPaths.MasterAddName}>
        <div className="bg-[#F5F7F7] dark:bg-[#0B1922] flex flex-col h-full overflow-hidden">
          {!hideHeader && (
            <div className="flex-shrink-0 p-2">
              <PageHeader
                title={getOrganizationTitle()}
                onBack={onGoBack}
                action={!isMasterIndex ? renderActionButtons() : undefined}
              />
            </div>
          )}

          <div className="!w-full px-4">
            {!hideHeader && tabs && (
              <NavigationButtonGroup
                tabs={tabsWithHandlers}
                activeTab={activeTab}
              />
            )}
          </div>

          <div className="flex-1 overflow-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {renderTabContent()}
          </div>
        </div>
        <Loader open={isLoading} />
      </TabAnimationWrapper>
      {!isMasterIndex && <DisabledOverlay />}
    </>
  );
};
